import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Mail, Server, Send, Eye, Save, FileText, Pencil,
  CheckCircle, AlertCircle, Loader2, Plus, Trash2, X, Code
} from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { id: 'smtp', label: 'SMTP Configuration', icon: Server },
  { id: 'templates', label: 'Email Templates', icon: FileText },
];

// ─── SMTP Tab ───────────────────────────────────────────
const SmtpTab = () => {
  const [config, setConfig] = useState({
    host: '', port: 587, username: '', password: '',
    encryption: 'tls', from_email: '', from_name: 'CoinPayUp',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/mailer/smtp-config`);
        setConfig({
          host: data.host || '', port: data.port || 587,
          username: data.username || '', password: '',
          encryption: data.encryption || 'tls',
          from_email: data.from_email || '', from_name: data.from_name || 'CoinPayUp',
        });
        setIsConfigured(data.is_configured);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    if (!config.host || !config.username) {
      toast.error('Host and username are required');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/mailer/smtp-config`, config);
      toast.success('SMTP configuration saved');
      setIsConfigured(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleTest = async () => {
    if (!testEmail) { toast.error('Enter a recipient email'); return; }
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/mailer/test-email`, { to_email: testEmail });
      toast.success(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test email failed');
    }
    setTesting(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6">
      <Card className="border-border bg-card" data-testid="smtp-config-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server className="h-5 w-5 text-indigo-400" />
              <CardTitle className="text-base">SMTP Server Settings</CardTitle>
            </div>
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${isConfigured ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'}`} data-testid="smtp-status-badge">
              {isConfigured ? <CheckCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
              {isConfigured ? 'Configured' : 'Not Configured'}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">SMTP Host</Label>
              <Input data-testid="smtp-host" placeholder="smtp.gmail.com" value={config.host}
                onChange={e => setConfig(p => ({ ...p, host: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Port</Label>
              <Input data-testid="smtp-port" type="number" placeholder="587" value={config.port}
                onChange={e => setConfig(p => ({ ...p, port: parseInt(e.target.value) || 587 }))} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Username / Email</Label>
              <Input data-testid="smtp-username" placeholder="your@email.com" value={config.username}
                onChange={e => setConfig(p => ({ ...p, username: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Password / App Password</Label>
              <Input data-testid="smtp-password" type="password" placeholder="Enter password" value={config.password}
                onChange={e => setConfig(p => ({ ...p, password: e.target.value }))} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Encryption</Label>
              <div className="flex gap-2" data-testid="smtp-encryption">
                {['tls', 'ssl', 'none'].map(enc => (
                  <button key={enc} onClick={() => setConfig(p => ({ ...p, encryption: enc }))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${config.encryption === enc ? 'bg-indigo-500/15 border-indigo-500/40 text-indigo-400' : 'bg-muted/30 border-border text-muted-foreground hover:text-foreground'}`}>
                    {enc.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">From Email</Label>
              <Input data-testid="smtp-from-email" placeholder="noreply@coinpayup.com" value={config.from_email}
                onChange={e => setConfig(p => ({ ...p, from_email: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">From Name</Label>
              <Input data-testid="smtp-from-name" placeholder="CoinPayUp" value={config.from_name}
                onChange={e => setConfig(p => ({ ...p, from_name: e.target.value }))} />
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} data-testid="smtp-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Test Email */}
      <Card className="border-border bg-card" data-testid="smtp-test-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Send className="h-5 w-5 text-cyan-400" />
            <CardTitle className="text-base">Send Test Email</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input data-testid="test-email-input" placeholder="recipient@example.com" value={testEmail}
              onChange={e => setTestEmail(e.target.value)} className="max-w-sm" />
            <Button onClick={handleTest} disabled={testing || !isConfigured} variant={isConfigured ? "default" : "secondary"} data-testid="send-test-btn">
              {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send Test
            </Button>
          </div>
          {!isConfigured && (
            <p className="text-xs text-amber-400 mt-2">Save SMTP settings first before sending a test email.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Templates Tab ──────────────────────────────────────
const TemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template obj being edited
  const [preview, setPreview] = useState(null); // { subject, body }
  const [creating, setCreating] = useState(false);
  const [newTpl, setNewTpl] = useState({ slug: '', name: '', subject: '', body: '', variables: '' });
  const [saving, setSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/mailer/templates`);
      setTemplates(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleToggle = async (tpl) => {
    try {
      await axios.put(`${API}/mailer/templates/${tpl.id}`, { is_active: !tpl.is_active });
      setTemplates(prev => prev.map(t => t.id === tpl.id ? { ...t, is_active: !t.is_active } : t));
      toast.success(`Template ${!tpl.is_active ? 'enabled' : 'disabled'}`);
    } catch { toast.error('Failed to update'); }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.put(`${API}/mailer/templates/${editing.id}`, {
        name: editing.name, subject: editing.subject, body: editing.body,
      });
      toast.success('Template saved');
      setEditing(null);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handlePreview = async (tpl) => {
    try {
      const { data } = await axios.post(`${API}/mailer/templates/${tpl.id}/preview`);
      setPreview(data);
    } catch { toast.error('Preview failed'); }
  };

  const handleCreate = async () => {
    if (!newTpl.slug || !newTpl.name || !newTpl.subject) {
      toast.error('Slug, name, and subject are required');
      return;
    }
    setSaving(true);
    try {
      const vars = newTpl.variables ? newTpl.variables.split(',').map(v => v.trim()).filter(Boolean) : [];
      await axios.post(`${API}/mailer/templates`, { ...newTpl, variables: vars });
      toast.success('Template created');
      setCreating(false);
      setNewTpl({ slug: '', name: '', subject: '', body: '', variables: '' });
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    }
    setSaving(false);
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await axios.delete(`${API}/mailer/templates/${tpl.id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  // ── Preview Modal ──
  if (preview) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Email Preview (Sample Data)</h3>
          <Button variant="ghost" size="sm" onClick={() => setPreview(null)} data-testid="close-preview-btn">
            <X className="h-4 w-4 mr-1" /> Close
          </Button>
        </div>
        <Card className="border-border bg-card" data-testid="template-preview">
          <CardContent className="pt-5">
            <p className="text-xs text-muted-foreground mb-1">Subject</p>
            <p className="text-sm font-semibold text-foreground mb-4">{preview.subject}</p>
            <div className="border border-border rounded-lg p-5 bg-background">
              <div dangerouslySetInnerHTML={{ __html: preview.body }} className="prose prose-sm dark:prose-invert max-w-none" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Edit Modal ──
  if (editing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Edit: {editing.name}</h3>
          <Button variant="ghost" size="sm" onClick={() => setEditing(null)}><X className="h-4 w-4 mr-1" /> Cancel</Button>
        </div>
        <Card className="border-border bg-card" data-testid="template-editor">
          <CardContent className="pt-5 space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Template Name</Label>
              <Input data-testid="edit-template-name" value={editing.name}
                onChange={e => setEditing(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Subject Line</Label>
              <Input data-testid="edit-template-subject" value={editing.subject}
                onChange={e => setEditing(p => ({ ...p, subject: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Body (HTML)</Label>
              <textarea data-testid="edit-template-body" value={editing.body}
                onChange={e => setEditing(p => ({ ...p, body: e.target.value }))}
                className="w-full h-64 px-3 py-2 text-sm bg-background border border-border rounded-lg font-mono resize-y focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            {editing.variables?.length > 0 && (
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Available Variables</Label>
                <div className="flex flex-wrap gap-1.5">
                  {editing.variables.map(v => (
                    <span key={v} className="px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-400 text-xs font-mono">{`{{${v}}}`}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => handlePreview(editing)} data-testid="preview-while-editing-btn">
                <Eye className="h-4 w-4 mr-1" /> Preview
              </Button>
              <Button size="sm" onClick={handleSaveEdit} disabled={saving} data-testid="save-template-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Save Template
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Create New Template ──
  if (creating) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">New Email Template</h3>
          <Button variant="ghost" size="sm" onClick={() => setCreating(false)}><X className="h-4 w-4 mr-1" /> Cancel</Button>
        </div>
        <Card className="border-border bg-card" data-testid="template-create-form">
          <CardContent className="pt-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Slug (unique key)</Label>
                <Input data-testid="new-template-slug" placeholder="welcome_email" value={newTpl.slug}
                  onChange={e => setNewTpl(p => ({ ...p, slug: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Name</Label>
                <Input data-testid="new-template-name" placeholder="Welcome Email" value={newTpl.name}
                  onChange={e => setNewTpl(p => ({ ...p, name: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Subject Line</Label>
              <Input data-testid="new-template-subject" placeholder="Welcome to CoinPayUp, {{merchant_name}}!" value={newTpl.subject}
                onChange={e => setNewTpl(p => ({ ...p, subject: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Body (HTML)</Label>
              <textarea data-testid="new-template-body" value={newTpl.body}
                onChange={e => setNewTpl(p => ({ ...p, body: e.target.value }))}
                className="w-full h-48 px-3 py-2 text-sm bg-background border border-border rounded-lg font-mono resize-y focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="<h2>Hello {{merchant_name}},</h2><p>...</p>" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Variables (comma-separated)</Label>
              <Input data-testid="new-template-variables" placeholder="merchant_name, amount, invoice_id" value={newTpl.variables}
                onChange={e => setNewTpl(p => ({ ...p, variables: e.target.value }))} />
            </div>
            <div className="flex justify-end">
              <Button size="sm" onClick={handleCreate} disabled={saving} data-testid="create-template-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                Create Template
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Template List ──
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{templates.length} template{templates.length !== 1 ? 's' : ''}</p>
        <Button size="sm" onClick={() => setCreating(true)} data-testid="add-template-btn">
          <Plus className="h-4 w-4 mr-1" /> New Template
        </Button>
      </div>

      <div className="space-y-3">
        {templates.map(tpl => (
          <Card key={tpl.id} className="border-border bg-card" data-testid={`template-card-${tpl.slug}`}>
            <CardContent className="py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className="h-4 w-4 text-indigo-400 shrink-0" />
                    <span className="text-sm font-semibold text-foreground">{tpl.name}</span>
                    <code className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">{tpl.slug}</code>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{tpl.subject}</p>
                  {tpl.variables?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {tpl.variables.slice(0, 5).map(v => (
                        <span key={v} className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono text-muted-foreground">{`{{${v}}}`}</span>
                      ))}
                      {tpl.variables.length > 5 && (
                        <span className="text-[10px] text-muted-foreground">+{tpl.variables.length - 5} more</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch checked={tpl.is_active} onCheckedChange={() => handleToggle(tpl)} data-testid={`toggle-${tpl.slug}`} />
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePreview(tpl)} data-testid={`preview-${tpl.slug}`}>
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing({ ...tpl })} data-testid={`edit-${tpl.slug}`}>
                    <Pencil className="h-4 w-4 text-muted-foreground" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(tpl)} data-testid={`delete-${tpl.slug}`}>
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────
const AdminMailer = () => {
  const [tab, setTab] = useState('smtp');

  return (
    <AdminLayout title="Mailer">
      <div className="space-y-6" data-testid="admin-mailer-page">
        <p className="text-sm text-muted-foreground -mt-1">Configure SMTP settings and manage email templates for automated notifications.</p>

        {/* Tab Buttons */}
        <div className="flex gap-1 p-1 bg-muted/30 rounded-xl w-fit" data-testid="mailer-tabs">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === t.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`} data-testid={`tab-${t.id}`}>
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === 'smtp' && <SmtpTab />}
        {tab === 'templates' && <TemplatesTab />}
      </div>
    </AdminLayout>
  );
};

export default AdminMailer;
